import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { getChangedLineIndices } from "./agentPayloadJsonDiff";
import { formatSortedAgentPayloadJson } from "./sortJsonForPreview";

const HIGHLIGHT_MS = 2000;
const FADE_DURATION_S = 1.35;

type AgentPayloadJsonViewProps = {
  value: Record<string, unknown>;
};

export function AgentPayloadJsonView({ value }: AgentPayloadJsonViewProps) {
  const reduceMotion = useReducedMotion();
  const text = formatSortedAgentPayloadJson(value);
  const lines = text.split("\n");

  const prevTextRef = useRef<string | null>(null);
  const [highlightedLines, setHighlightedLines] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const previous = prevTextRef.current;
    if (previous === null) {
      prevTextRef.current = text;
      return;
    }
    if (previous === text) {
      return;
    }

    prevTextRef.current = text;
    const changed = getChangedLineIndices(previous, text);
    if (changed.size === 0) {
      return;
    }

    setHighlightedLines(changed);
    const timeoutId = window.setTimeout(() => {
      setHighlightedLines(new Set());
    }, HIGHLIGHT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [text]);

  return (
    <div
      className="max-h-72 overflow-auto rounded bg-slate-900/50 p-2 font-mono text-[10px] leading-relaxed text-gray-400"
      data-testid="developer-menu-agent-payload-json"
    >
      {lines.map((line, index) => {
        const isHighlighted = highlightedLines.has(index);
        return (
          <motion.div
            key={index}
            className="whitespace-pre-wrap rounded-sm px-0.5 -mx-0.5"
            data-highlighted={isHighlighted ? "true" : undefined}
            initial={false}
            animate={{
              backgroundColor:
                isHighlighted && !reduceMotion
                  ? "rgba(52, 211, 153, 0.28)"
                  : "rgba(0, 0, 0, 0)",
            }}
            transition={{
              duration: isHighlighted && !reduceMotion ? FADE_DURATION_S : 0,
              ease: "easeOut",
            }}
          >
            {line.length > 0 ? line : "\u00a0"}
          </motion.div>
        );
      })}
    </div>
  );
}
